import { faWaveformLines } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faMicrophone,
  faPlay,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src';
import {
  formatTime,
  getAudioRecorder,
  useRecordingTimer,
} from '@repo/videa-notes';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '@/app/appHeader/imageViewerMenu/atoms';
import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';
import {
  VideaNotesUIState,
  useOpenVideaNotes,
} from '@/modules/videaNotes/helpers';
import { useExistingNotes } from '@/react-query/videa-notes/useExistingNotes';

import { appointmentPatientsAtom } from '../../patientList/atoms';
import {
  isActiveAnnotatorRouteAtom,
  isNoteGenerationActiveAtom,
  isRecorderSheetOpenAtom,
  isRecordingSessionActiveAtom,
  isRecordingSessionPausedAtom,
  recordingDurationAtom,
} from '../state';
import { VoiceNotesSource } from '../types';

export const VideaNotesButton = () => {
  // Redux selectors
  const user = useSelector((state: any) => state?.login?.user);
  const patients = useSelector((state: any) => state.annotator?.patients) || {};

  // atoms
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isRecordingSessionPaused = useAtomValue(isRecordingSessionPausedAtom);
  const isNoteGenerationActive = useAtomValue(isNoteGenerationActiveAtom);
  const isActiveAnnotatorRoute = useAtomValue(isActiveAnnotatorRouteAtom);
  const duration = useAtomValue(recordingDurationAtom);
  const appointmentPatients = useAtomValue(appointmentPatientsAtom);
  const estimatedVisitIdAtomValue: string | null =
    useAtomValue(selectedVisitAtom);
  const unifiedAppointmentId = useAtomValue(selectedUnifiedIdAtom);

  const setIsVideaNotesSheetOpen = useSetAtom(isRecorderSheetOpenAtom);

  // hooks
  const openVideaNotes = useOpenVideaNotes();
  const location = useLocation();

  useRecordingTimer(); // hook for the recording duration timer

  // contextual data for Videa Notes feature
  const imagingPatientId = location.pathname.split('/')[2];

  let matchingPatient = appointmentPatients.filter(
    (p) => p.imagingPatientId === imagingPatientId,
  );

  const secondaryMatchingPatient = patients[imagingPatientId] || {};

  const estimatedVisitId = estimatedVisitIdAtomValue
    ? parseInt(estimatedVisitIdAtomValue)
    : null;

  // Check for existing notes for this appointment
  const { data: existingNotes = [] } = useExistingNotes(
    unifiedAppointmentId || undefined,
    isActiveAnnotatorRoute &&
      !isRecordingSessionActive &&
      !!unifiedAppointmentId,
  );

  // Check if button should be disabled
  const isButtonDisabled = isActiveAnnotatorRoute && !unifiedAppointmentId;

  // handlers
  const handleOpenVideaNotes = () => {
    if (isActiveAnnotatorRoute && !isRecordingSessionActive) {
      // Early return if no unified appointment ID
      if (!unifiedAppointmentId) {
        return;
      }

      // Check if we have a matching patient before proceeding
      if (!matchingPatient || matchingPatient.length === 0) {
        if (secondaryMatchingPatient.name) {
          matchingPatient = [
            {
              firstName: secondaryMatchingPatient.name,
              videaId: imagingPatientId,
            },
          ];
        } else {
          return;
        }
      }

      const patient = matchingPatient[0];

      // Create a complete appointment object matching the Appointment interface
      const appointmentData: VideaNotesAppointment = {
        patient,
        appointment: {
          unifiedId: unifiedAppointmentId,
          start: new Date().toISOString(), // Required field, using current date as fallback
          estimatedVisitId: estimatedVisitId,
        },
        viewed: false, // Required field
        lastTreatments: {
          lastBw: null,
          lastFluoride: null,
          lastFmx: null,
          lastPerioChart: null,
          lastPerioMaintenance: null,
          lastProphy: null,
          lastSrp: null,
        },
      };

      // If there are existing notes, open to SUMMARY_RESULT view
      // Otherwise, open to AUDIO_CAPTURE view
      const uiState =
        existingNotes.length > 0
          ? VideaNotesUIState.SUMMARY_RESULT
          : VideaNotesUIState.AUDIO_CAPTURE;
      openVideaNotes(
        user,
        appointmentData,
        uiState,
        VoiceNotesSource.VOICE_NOTES_BUTTON,
      );
    } else {
      setIsVideaNotesSheetOpen(true);
    }
  };

  const handleResumeRecordingSession = () => {
    getAudioRecorder().resume?.();
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <>
            {/* a recording has been started but is currently paused */}
            {isRecordingSessionPaused && (
              <Button
                className="lg:vai-w-[208px]"
                variant="destructive"
                onClick={handleResumeRecordingSession}
              >
                <FontAwesomeIcon
                  icon={faPlay}
                  className="vai-h-4 vai-w-4 vai-text-white"
                />{' '}
                Resume
              </Button>
            )}

            {/* a recording is in an active state */}
            {isRecordingSessionActive && !isRecordingSessionPaused && (
              <Button
                className="lg:vai-w-[208px]"
                variant="destructivePulse"
                onClick={handleOpenVideaNotes}
              >
                <FontAwesomeIcon
                  icon={faWaveformLines}
                  className="vai-h-4 vai-w-4 vai-text-white"
                />
                {` ${formatTime(duration)}`}
              </Button>
            )}

            {/* generation of a clinical note has been requested, stopping the recording session */}
            {isNoteGenerationActive && (
              <Button
                className="vai-w-[208px]"
                variant="destructive"
                onClick={handleOpenVideaNotes}
              >
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="vai-h-4 vai-w-4 vai-text-white vai-animate-spin"
                />
                Processing
              </Button>
            )}

            {/* base case, where no active recording session exists */}
            {!isRecordingSessionActive && !isNoteGenerationActive && (
              <Button
                variant="outline"
                className="vai-bg-brand-primary hover:vai-bg-brand-primary/90 vai-text-black hover:vai-text-black vai-border-none"
                onClick={handleOpenVideaNotes}
                disabled={isButtonDisabled}
              >
                <FontAwesomeIcon
                  icon={faMicrophone}
                  className="vai-h-4 vai-w-4 vai-text-black"
                />
                {' Start Note'}
              </Button>
            )}
          </>
        </TooltipTrigger>
        <TooltipContent>
          <span>
            <Trans>Videa Notes</Trans>
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
